import React from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import CopyRightInfo from "../../../../core/common/copyRightInfo";
import Table from "../../../../core/common/dataTable/index";
import SettingsMenu from "./common/setting-menu";
import PreLoader from "../../../../core/common/preLoader";
import CustomToast from "../../../../core/common/customToast";
import useFetch from "../../../../hooks/useFetch";
import { API } from "../../../../common/Constants";
import { getAPIHeaders } from "../../../../common/Utils";
import CommonTabMenu from "../commonTabMenu";

interface StorageFile {
  fileId: number;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  fileSizeMB: number;
  fileSizeGB: number;
  uploadedBy: number;
  uploadedByName: string;
  uploadedAt: number;
}

interface FileTypeBreakdown {
  pdfCount: number;
  pdfSizeBytes: number;
  pdfSizeMB: number;
  imageCount: number;
  imageSizeBytes: number;
  imageSizeMB: number;
  videoCount: number;
  videoSizeBytes: number;
  videoSizeMB: number;
  documentCount: number;
  documentSizeBytes: number;
  documentSizeMB: number;
  otherCount: number;
  otherSizeBytes: number;
  otherSizeMB: number;
}

interface StorageAnalytics {
  entityType: string;
  description: string;
  totalFiles: number;
  totalSizeBytes: number;
  totalSizeMB: number;
  totalSizeGB: number;
  files: StorageFile[];
  breakdown: any[];
  statistics: {
    fileTypeBreakdown: FileTypeBreakdown;
  };
}

const StorageUsage = () => {
  const routes = all_routes;
  const { isLoading, error, callFetch } = useFetch();
  const [storageData, setStorageData] = React.useState<StorageAnalytics | null>(null);
  const [toastSettings, setToastSettings] = React.useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO" | "";
  }>({
    show: false,
    message: "",
    heading: "",
    type: "ERROR",
  });

  React.useEffect(() => {
    fetchStorageAnalytics();
  }, []);

  React.useEffect(() => {
    if (error) {
      if (error.response?.data) {
        setToastSettings({
          message: error?.response?.data,
          heading: "Ooops",
          show: true,
          type: "ERROR"
        })
      } else {
        setToastSettings({
          message: 'Something went wrong please try again later.',
          heading: "Ooops",
          show: true,
          type: "ERROR"
        })
      }
    }
  }, [error]);

  const fetchStorageAnalytics = () => {
    callFetch(API.STORAGE_ANALYTICS,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((res) => {
      setStorageData(res);
    }).catch((err) => {});
  }

  const onToastClose = () => {
    setToastSettings({
      show: false,
      message: "",
      heading: "",
      type: "",
    });
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      title: "File Name",
      dataIndex: "fileName",
      sorter: (a: StorageFile, b: StorageFile) => a.fileName?.localeCompare(b.fileName),
      defaultSortOrder: "ascend" as const,
      render: (text: string) => {
        // Decode URL-encoded file names
        try {
          return decodeURIComponent(text);
        } catch {
          return text;
        }
      }
    },
    {
      title: "File Type",
      dataIndex: "fileType",
      sorter: (a: StorageFile, b: StorageFile) => a.fileType?.localeCompare(b.fileType),
    },
    {
      title: "Size (MB)",
      dataIndex: "fileSizeMB",
      sorter: (a: StorageFile, b: StorageFile) => a.fileSizeMB - b.fileSizeMB,
      render: (value: number) => value.toFixed(2),
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedByName",
      sorter: (a: StorageFile, b: StorageFile) => a.uploadedByName?.localeCompare(b.uploadedByName),
    },
    {
      title: "Uploaded At",
      dataIndex: "uploadedAt",
      sorter: (a: StorageFile, b: StorageFile) => a.uploadedAt - b.uploadedAt,
      render: (timestamp: number) => { return formatDate(timestamp) },
    }
  ];

  const renderStorageSummary = () => {
    if (!storageData) return null;

    return (
      <div className="border rounded p-3 mb-3">
        <h5 className="mb-3">Storage Summary</h5>
        <div className="row">
          <div className="col-md-3 mb-2">
            <div className="d-flex flex-column">
              <span className="text-muted small">Total Files</span>
              <span className="fw-bold fs-16 text-primary">{storageData.totalFiles}</span>
            </div>
          </div>
          <div className="col-md-3 mb-2">
            <div className="d-flex flex-column">
              <span className="text-muted small">Total Size (MB)</span>
              <span className="fw-bold fs-16 text-primary">{storageData.totalSizeMB.toFixed(2)} MB</span>
            </div>
          </div>
          <div className="col-md-3 mb-2">
            <div className="d-flex flex-column">
              <span className="text-muted small">Total Size (GB)</span>
              <span className="fw-bold fs-16 text-primary">{storageData.totalSizeGB.toFixed(2)} GB</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFileTypeBreakdown = () => {
    if (!storageData?.statistics?.fileTypeBreakdown) return null;

    const breakdown = storageData.statistics.fileTypeBreakdown;

    return (
      <div className="border rounded p-3 mb-3">
        <h5 className="mb-3">File Type Breakdown</h5>
        <div className="row">
          <div className="col-md-6 mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">PDF Files</span>
              <div className="text-end">
                <span className="fw-bold">{breakdown.pdfCount}</span>
                <span className="text-muted ms-2">({breakdown.pdfSizeMB.toFixed(2)} MB)</span>
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Image Files</span>
              <div className="text-end">
                <span className="fw-bold">{breakdown.imageCount}</span>
                <span className="text-muted ms-2">({breakdown.imageSizeMB.toFixed(2)} MB)</span>
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Video Files</span>
              <div className="text-end">
                <span className="fw-bold">{breakdown.videoCount}</span>
                <span className="text-muted ms-2">({breakdown.videoSizeMB.toFixed(2)} MB)</span>
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Document Files</span>
              <div className="text-end">
                <span className="fw-bold">{breakdown.documentCount}</span>
                <span className="text-muted ms-2">({breakdown.documentSizeMB.toFixed(2)} MB)</span>
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Other Files</span>
              <div className="text-end">
                <span className="fw-bold">{breakdown.otherCount}</span>
                <span className="text-muted ms-2">({breakdown.otherSizeMB.toFixed(2)} MB)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <>
        <CustomToast
          show={toastSettings?.show}
          message={toastSettings?.message}
          heading={toastSettings?.heading}
          type={toastSettings?.type || "INFO"}
          onClose={onToastClose} />
        <PreLoader showLoader={isLoading} />
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            {/* Breadcrumb */}
            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
              <div className="my-auto mb-2">
                <h2 className="mb-1">Storage Settings</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.adminDashboard}>
                        <i className="ti ti-smart-home" />
                      </Link>
                    </li>
                    <li className="breadcrumb-item">Administration</li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Settings
                    </li>
                  </ol>
                </nav>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
            {/* /Breadcrumb */}
            <CommonTabMenu activeTab="Storage" />
            <div className="row">
              <div className="col-xl-3 theiaStickySidebar">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex flex-column list-group settings-list">
                      <SettingsMenu activeTab="StorageUsage" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-9">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-3">
                      <h4>Storage Usage</h4>
                    </div>
                    {renderStorageSummary()}
                    {renderFileTypeBreakdown()}
                    <div className="mt-3">
                      <h5 className="mb-3">File List</h5>
                      <Table 
                        dataSource={storageData?.files || []} 
                        columns={columns} 
                        Selection={false} 
                        refresh={fetchStorageAnalytics}
                        rowKey="fileId"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
            <CopyRightInfo/>
          </div>
        </div>
        {/* /Page Wrapper */}
      </>
    </div>
  );
};

export default StorageUsage;

